import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { createStoreApp, updateStoreApp, uploadPlayKey, useRefreshStores, type StoreApp } from '@/api/endpointsStores';
import type { SigningCredential } from '@/api/endpointsSigning';
import { FormField, fieldA11y } from '@/components/FormField';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { showApiFailure } from '@/features/access/accessToasts';
import { PlaySetupSteps } from './PlaySetupSteps';
import { UPLOAD_KEY, newStoreAppForm, storeAppErrors, storeAppFormOf, type StoreAppFormValues } from './storesModel';

interface Props {
  appId: string;
  keys: SigningCredential[];
  /** The store app to change, or null to add one. */
  editing: StoreApp | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Adds or changes an app's Google Play listing (the organization's owners and admins, and platform operators; the API refuses anyone else). */
export function StoreAppDialog({ appId, keys, editing, open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="store-app-dialog" className="sm:max-w-lg" aria-describedby="store-app-desc">
        {open ? <StoreAppForm appId={appId} keys={keys} editing={editing} onDone={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function useStoreAppSubmit(appId: string, editing: StoreApp | null, onDone: () => void) {
  const refresh = useRefreshStores(appId);
  const [pending, setPending] = useState(false);
  const [keyIdempotency] = useState(() => crypto.randomUUID());
  const save = async (v: StoreAppFormValues, file: File | null) => {
    setPending(true);
    try {
      const credentialId = v.keyId === UPLOAD_KEY && file ? (await uploadPlayKey(v.newKeyName.trim(), file, keyIdempotency)).id : v.keyId;
      const settings = { credentialId, isPrivateManaged: v.isPrivateManaged, managedPublishing: v.managedPublishing };
      const saved = editing ? await updateStoreApp(editing.id, settings) : await createStoreApp({ appId, platform: 'android', identifier: v.identifier.trim(), ...settings });
      toast.success(editing ? `Google Play app ${saved.identifier} saved` : `Google Play app ${saved.identifier} added`, { description: 'Set which tracks each environment may reach under Rules.' });
      refresh();
      onDone();
    } catch (err) {
      refresh();
      showApiFailure(err, editing ? 'Could not save the Google Play app' : 'Could not add the Google Play app');
    } finally {
      setPending(false);
    }
  };
  return { save, pending };
}

function StoreAppForm({ appId, keys, editing, onDone }: { appId: string; keys: SigningCredential[]; editing: StoreApp | null; onDone: () => void }) {
  const [values, setValues] = useState<StoreAppFormValues>(() => (editing ? storeAppFormOf(editing) : newStoreAppForm(keys)));
  const [file, setFile] = useState<File | null>(null);
  const [touched, setTouched] = useState(false);
  const { save, pending } = useStoreAppSubmit(appId, editing, onDone);
  const errors = touched ? storeAppErrors(values, Boolean(editing), file) : {};
  const set = (patch: Partial<StoreAppFormValues>) => setValues((v) => ({ ...v, ...patch }));
  const idHint = editing ? 'A listing keeps its package name' : 'The applicationId Google Play knows the app by';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(storeAppErrors(values, Boolean(editing), file)).length) return;
    void save(values, file);
  };
  return (
    <form onSubmit={submit} noValidate className="flex min-h-0 flex-col">
      <DialogHeader>
        <DialogTitle>{editing ? `Google Play · ${editing.identifier}` : 'Add a Google Play app'}</DialogTitle>
        <DialogDescription id="store-app-desc">The app&apos;s listing in Google Play and the service account key Hangar submits with. The key is checked, encrypted and never shown again.</DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        {editing ? null : <PlaySetupSteps defaultOpen={keys.length === 0} />}
        <FormField id="store-app-identifier" label="Package name" required={!editing} error={errors.identifier} hint={idHint}>
          <Input data-testid="store-app-identifier" value={values.identifier} disabled={Boolean(editing)} onChange={(e) => set({ identifier: e.target.value })} placeholder="com.client.app" className="font-mono" spellCheck={false} autoComplete="off" {...fieldA11y('store-app-identifier', errors.identifier, idHint)} />
        </FormField>
        <KeyField values={values} keys={keys} set={set} file={file} setFile={setFile} error={errors.key} />
        <label className="flex min-h-11 items-center justify-between gap-3 text-[13px] sm:min-h-0">
          <span>Private app in Managed Google Play<span className="block text-xs text-subtle">Only the client&apos;s organization sees it (for example Hibbett)</span></span>
          <Switch data-testid="store-app-private" checked={values.isPrivateManaged} onCheckedChange={(isPrivateManaged) => set({ isPrivateManaged })} />
        </label>
        <label className="flex min-h-11 items-center justify-between gap-3 text-[13px] sm:min-h-0">
          <span>Managed publishing<span className="block text-xs text-subtle">Google Play holds approved releases until someone publishes them</span></span>
          <Switch data-testid="store-app-managed-publishing" checked={values.managedPublishing} onCheckedChange={(managedPublishing) => set({ managedPublishing })} />
        </label>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone} className="max-sm:h-11">Cancel</Button>
        <Button type="submit" data-testid="store-app-save" loading={pending} className="max-sm:h-11">{editing ? 'Save' : 'Add app'}</Button>
      </DialogFooter>
    </form>
  );
}

interface KeyFieldProps {
  values: StoreAppFormValues;
  keys: SigningCredential[];
  set: (patch: Partial<StoreAppFormValues>) => void;
  file: File | null;
  setFile: (file: File | null) => void;
  error: string | undefined;
}

/** An existing service account key of the organization, or a new one uploaded with this app. */
function KeyField({ values, keys, set, file, setFile, error }: KeyFieldProps) {
  const label = (k: SigningCredential) => `${k.name}${typeof k.metadata.clientEmail === 'string' ? ` · ${k.metadata.clientEmail}` : ''}`;
  return (
    <FormField id="store-app-key" label="Service account key" required error={error} hint="A key with release rights on this app in Play Console">
      <div className="flex flex-col gap-2">
        <Select value={values.keyId} onValueChange={(keyId) => keyId && set({ keyId })}>
          <SelectTrigger data-testid="store-app-key" className="w-full min-w-0" aria-label="Service account key">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {keys.map((k) => <SelectItem key={k.id} value={k.id}>{label(k)}</SelectItem>)}
            <SelectItem value={UPLOAD_KEY}>Upload a new key (.json)</SelectItem>
          </SelectContent>
        </Select>
        {values.keyId === UPLOAD_KEY ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <Input data-testid="store-app-key-name" value={values.newKeyName} onChange={(e) => set({ newKeyName: e.target.value })} aria-label="Key name" maxLength={120} />
            <Input data-testid="store-app-key-file" type="file" accept="application/json,.json" aria-label="Service account key file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            {file ? <span className="text-xs text-subtle sm:col-span-2">{file.name}</span> : null}
          </div>
        ) : null}
      </div>
    </FormField>
  );
}
